import { useLocalization } from "@fluent/react";
import { LoadingShim } from "@narmi/design_system";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { StatusCard } from "@lending/components/StatusTimeline";
import { useGetConsumerApplicationQuery } from "@lending/hooks/queries/useGetConsumerApplicationQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import { useProductType } from "@lending/hooks/useProductType";
import { useToastsContext } from "@lending/hooks/useToasts";
import { OpenedTimelineContent, ReviewTimelineContent } from "./components";
import { isReviewState } from "./isReviewState";
import styles from "./Status.module.css";
import { useStore } from "../../../../contexts/StoreContext";
import { useMembershipShareApplication } from "../hooks/useMembershipShareApplication";
import type { AccountApplication } from "./types";

type InstitutionWithDaoEnrollUrl = ReturnType<typeof useGetInstitutionQuery>["institution"] & {
  dao_success_screen_existing_customer_enroll_url?: string;
  dao_success_screen_new_customer_enroll_url?: string;
};

function Status() {
  const { l10n } = useLocalization();
  const { data } = useStore();
  const routeProductType = useProductType();
  const {
    application,
    isLoading: isApplicationLoading,
    isError: hasApplicationError,
  } = useMembershipShareApplication();
  const accountApplicationUuid =
    application?.account_application_uuid ?? data.accountApplicationUuid;
  const productType = routeProductType ?? data.productType ?? "personal-loan";

  let heading: string;
  if (productType === "credit-card") {
    heading = l10n.getString("membership-share-status-heading-credit-card", null, "You're all set");
  } else if (productType === "auto-loan") {
    heading = l10n.getString("membership-share-status-heading-auto-loan", null, "You're all set");
  } else {
    heading = l10n.getString(
      "membership-share-status-heading-personal-loan",
      null,
      "You're all set"
    );
  }

  const { institution } = useGetInstitutionQuery();
  const navigate = useNavigateWithSearch();
  const { showErrorToast } = useToastsContext();
  const aoApplicationQuery = useGetConsumerApplicationQuery(accountApplicationUuid);
  const [accountApplication, setAccountApplication] = useState<AccountApplication | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const institutionWithDaoEnrollUrl = institution as InstitutionWithDaoEnrollUrl;

  const enrollUrl = accountApplication?.is_existing_on_core
    ? institutionWithDaoEnrollUrl.dao_success_screen_existing_customer_enroll_url
    : (accountApplication?.autopopulated_enrollment_link ??
      institutionWithDaoEnrollUrl.dao_success_screen_new_customer_enroll_url);

  const enrollButtonLabel = accountApplication?.is_existing_on_core
    ? l10n.getString("membership-share-status-log-in", null, "Log in")
    : l10n.getString("membership-share-status-enroll-now", null, "Enroll now");

  useEffect(() => {
    if (!accountApplicationUuid) {
      if (isApplicationLoading) {
        return;
      }

      Sentry.captureMessage("Missing accountApplicationUuid on Status page", {
        level: hasApplicationError ? "error" : "warning",
      });
      setHasError(true);
      setIsLoading(false);
      return;
    }

    if (aoApplicationQuery.isLoading) {
      setIsLoading(true);
      return;
    }

    if (aoApplicationQuery.isError || !aoApplicationQuery.data) {
      Sentry.captureException(aoApplicationQuery.error);
      showErrorToast(
        l10n.getString(
          "membership-share-status-fetch-error",
          null,
          "There was an error loading your application status. Please try again."
        )
      );
      setHasError(true);
      setIsLoading(false);
      return;
    }

    if (aoApplicationQuery.data.state === "docs_requested") {
      void navigate("../documents-requested", { replace: true });
      return;
    }

    setAccountApplication(aoApplicationQuery.data);
    setHasError(false);
    setIsLoading(false);
  }, [
    aoApplicationQuery.data,
    aoApplicationQuery.error,
    aoApplicationQuery.isError,
    aoApplicationQuery.isLoading,
    accountApplicationUuid,
    hasApplicationError,
    isApplicationLoading,
    l10n,
    navigate,
    showErrorToast,
  ]);

  const handleEnrollNow = () => {
    if (!enrollUrl) {
      Sentry.captureMessage("Missing enrollment URL on membership share status page", {
        level: "error",
        extra: {
          accountApplicationUuid,
          accountApplicationState: accountApplication?.state,
        },
      });
      showErrorToast(
        l10n.getString(
          "membership-share-status-enroll-error",
          null,
          "There was an error opening enrollment. Please try again."
        )
      );
      return;
    }

    window.open(enrollUrl, "_blank", "noopener,noreferrer");
  };
  if (isLoading) {
    return (
      <PageLayout className={styles.pageLayout}>
        <LoadingShim isLoading />
      </PageLayout>
    );
  }

  if (hasError) {
    return <ErrorScreen />;
  }

  return (
    <PageLayout className={styles.pageLayout}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className="fontFamily--heading">{heading}</h1>
          </div>

          {accountApplication?.state === "opened" && (
            <OpenedTimelineContent
              accountApplication={accountApplication}
              enrollButtonLabel={enrollButtonLabel}
              onEnrollNow={handleEnrollNow}
              productType={productType}
            />
          )}

          {accountApplication && isReviewState(accountApplication.state) && (
            <ReviewTimelineContent productType={productType} />
          )}

          {(!accountApplication ||
            (accountApplication.state !== "opened" &&
              !isReviewState(accountApplication.state))) && (
            <StatusCard
              title={l10n.getString(
                "membership-share-status-fallback-title",
                null,
                "Loan application submitted"
              )}
              description={l10n.getString(
                "membership-share-status-fallback-description",
                null,
                "A loan officer will be in touch to complete processing your application."
              )}
            />
          )}
        </div>
      </div>
    </PageLayout>
  );
}

export { Status };
